import { useEffect, useRef } from "react";

const COLORS = [
  "rgba(255,182,193,0.7)",
  "rgba(255,192,203,0.6)",
  "rgba(255,228,225,0.5)",
  "rgba(255,240,245,0.5)",
];

// 🔑 Create heart with size locked to mobile reference
function createHeart(width, height) {
  const BASE_WIDTH = 375; // mobile reference
  const scale = Math.min(width / BASE_WIDTH, 1); // never grow beyond mobile size

  return {
    x: Math.random() * width,
    y: Math.random() * height,

    size: (10 + Math.random() * 12) * scale,

    vx: (Math.random() - 0.5) * 0.6,
    vy: (Math.random() - 0.5) * 0.6,

    driftChange: 0.002 + Math.random() * 0.003,

    angle: Math.random() * Math.PI * 2,
    rotation: (Math.random() - 0.5) * 0.01,

    opacity: 0.4 + Math.random() * 0.3,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
  };
}

export default function HeartAmbient({ trigger, count = 10 }) {
  const canvasRef = useRef(null);
  const hearts = useRef([]);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!trigger) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    let width = window.innerWidth;
    let height = window.innerHeight;

    canvas.width = width;
    canvas.height = height;

    // 🔥 Adjust density based on screen size (optional but recommended)
    const BASE_AREA = 375 * 667;
    const area = width * height;
    const adjustedCount = Math.max(
      count,
      Math.floor((area / BASE_AREA) * count),
    );

    hearts.current = Array.from({ length: adjustedCount }, () =>
      createHeart(width, height),
    );

    function drawHeart(h) {
      ctx.save();
      ctx.translate(h.x, h.y);
      ctx.rotate(h.angle);
      ctx.globalAlpha = h.opacity;

      const s = h.size;

      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.bezierCurveTo(s / 2, -s / 2, s, s / 3, 0, s);
      ctx.bezierCurveTo(-s, s / 3, -s / 2, -s / 2, 0, 0);

      ctx.fillStyle = h.color;
      ctx.fill();

      ctx.restore();
    }

    function animate() {
      ctx.clearRect(0, 0, width, height);

      hearts.current.forEach((h) => {
        // movement
        h.x += h.vx;
        h.y += h.vy;

        // organic drift
        h.vx += (Math.random() - 0.5) * h.driftChange;
        h.vy += (Math.random() - 0.5) * h.driftChange;

        // clamp speed
        h.vx = Math.max(-0.6, Math.min(0.6, h.vx));
        h.vy = Math.max(-0.6, Math.min(0.6, h.vy));

        // bounce edges
        if (h.x < 0 || h.x > width) h.vx *= -1;
        if (h.y < 0 || h.y > height) h.vy *= -1;

        // rotation
        h.angle += h.rotation;

        drawHeart(h);
      });

      animationRef.current = requestAnimationFrame(animate);
    }

    animate();

    // 🔁 Handle resize properly
    function handleResize() {
      width = window.innerWidth;
      height = window.innerHeight;

      canvas.width = width;
      canvas.height = height;

      hearts.current = hearts.current.map(() => createHeart(width, height));
    }

    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener("resize", handleResize);
    };
  }, [trigger, count]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 9999,
      }}
    />
  );
}
